import { useState } from "react";
import { Loader2, Bell } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const today = () => new Date().toISOString().slice(0, 10);

const REPEATS = [
  { v: "none", label: "Does not repeat" },
  { v: "daily", label: "Daily" },
  { v: "weekly", label: "Weekly" },
  { v: "monthly", label: "Monthly" },
];

export function ReminderDialog({ open, onOpenChange, entityType, entityId, onSaved }) {
  const [date, setDate] = useState(today());
  const [time, setTime] = useState("09:00");
  const [note, setNote] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [recurrence, setRecurrence] = useState("none");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!note.trim()) { toast.error("Add a short task note"); return; }
    if (!date || !time) { toast.error("Pick a date and time"); return; }
    if (new Date(`${date}T${time}`).getTime() < Date.now()) { toast.error("Pick a future date & time"); return; }
    setSaving(true);
    try {
      await api.post("/follow-ups", {
        entity_type: entityType, entity_id: entityId,
        due_date: date, due_time: time, note: note.trim(), next_action: nextAction.trim(),
        recurrence,
      });
      toast.success(recurrence === "none" ? "Reminder scheduled" : `Recurring reminder set (${recurrence})`);
      setNote(""); setNextAction(""); setRecurrence("none");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reminder-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Bell className="h-4 w-4 text-brand" /> Schedule a Reminder</DialogTitle>
          <DialogDescription>You'll get an in-app alert and a push notification at the set time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={date} min={today()} onChange={(e) => setDate(e.target.value)} className="h-9" data-testid="reminder-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Time (IST) <span className="text-red-500">*</span></Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-9" data-testid="reminder-time" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Task / Note <span className="text-red-500">*</span></Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. Call back with the revised BOQ" data-testid="reminder-note" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Next action (optional)</Label>
            <Input value={nextAction} onChange={(e) => setNextAction(e.target.value)} className="h-9" placeholder="e.g. Send sample box" data-testid="reminder-next-action" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Repeat</Label>
            <Select value={recurrence} onValueChange={setRecurrence}>
              <SelectTrigger className="h-9" data-testid="reminder-recurrence"><SelectValue /></SelectTrigger>
              <SelectContent>{REPEATS.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            {recurrence !== "none" && <p className="text-[11px] text-slate-400">Repeats {recurrence} until you mark it done or delete it.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reminder-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-brand hover:bg-brand/90 text-white gap-1.5" data-testid="reminder-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
